import type { Toast } from '@/types/toasts';
import { ref } from 'vue';
import type { RouteLocationNamedRaw } from 'vue-router';
import { i18n } from '@/plugins/i18n';

const DEFAULT_TIMEOUT = 5000;
const MAX_TOASTS = 5;

export const linkIsRouteLocation = (link: string | RouteLocationNamedRaw | undefined): link is RouteLocationNamedRaw => {
    return !!link && typeof link !== 'string';
};

export const toasts = ref<Toast[]>([]);

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export function useToasts() {
    function addToast(toast: Omit<Toast, 'uuid'>, timeout: number = DEFAULT_TIMEOUT) {
        const _toast: Toast = {
            ...toast,
            uuid: crypto.randomUUID(),
        };

        if (toasts.value.length >= MAX_TOASTS) {
            if (toasts.value[0]) removeToast(toasts.value[0].uuid);
        }

        toasts.value.push(_toast);

        const timerId = setTimeout(() => {
            removeToast(_toast.uuid);
        }, timeout);
        toastTimeouts.set(_toast.uuid, timerId);
    };

    function removeToast(uuid: string) {
        const timerId = toastTimeouts.get(uuid);
        if (timerId) {
            clearTimeout(timerId);
            toastTimeouts.delete(uuid);
        }

        const index = toasts.value.findIndex((toast) => toast.uuid === uuid);
        if (index !== -1) {
            toasts.value.splice(index, 1);
        }
    }

    function showGenericError() {
        addToast({
            type: 'error',
            title: i18n.global.t('genericError.title'),
            description: i18n.global.t('genericError.message'),
        });
    }

    return {
        toasts,
        addToast,
        removeToast,
        showGenericError,
    };
};
